import axios, { type AxiosRequestConfig } from 'axios';
import { getApiErrorMessage } from './error';

export type AuthRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
  _skipAuthRedirect?: boolean;
};

const apiConfig = {
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: 10000,
};

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  request: AuthRequestConfig;
}> = [];

function subscribeRefresh(
  resolve: (value: unknown) => void,
  reject: (reason?: unknown) => void,
  request: AuthRequestConfig,
) {
  refreshQueue.push({ resolve, reject, request });
}

function onRefreshed() {
  refreshQueue.forEach(({ resolve, reject, request }) => {
    api(request).then(resolve).catch(reject);
  });
  refreshQueue = [];
}

function onRefreshFailed(error: unknown) {
  refreshQueue.forEach(({ reject }) => reject(error));
  refreshQueue = [];
}

function redirectToLogin() {
  const pathname = window.location.pathname;

  if (pathname === '/entrar' || pathname === '/cadastro' || pathname === '/auth/magic-link') {
    return;
  }

  const from = `${pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(`/entrar?from=${encodeURIComponent(from)}`);
}

export const api = axios.create(apiConfig);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    error.message = getApiErrorMessage(error, error?.message);
    const originalRequest = error?.config as AuthRequestConfig | undefined;

    if (!originalRequest || error?.response?.status !== 401) {
      throw error;
    }

    // Requests explicitly marked to skip refresh should fail fast.
    if (originalRequest._skipAuthRefresh) {
      throw error;
    }

    const requestUrl = String(error?.config?.url ?? '');
    if (requestUrl.includes('/auth/refresh-token')) {
      if (!originalRequest._skipAuthRedirect) {
        redirectToLogin();
      }
      throw error;
    }

    if (originalRequest._retry) {
      if (!originalRequest._skipAuthRedirect) {
        redirectToLogin();
      }
      throw error;
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeRefresh(resolve, reject, originalRequest);
      });
    }

    isRefreshing = true;

    try {
      await api.post('/auth/refresh-token', {}, {
        _skipAuthRedirect: originalRequest._skipAuthRedirect,
        _skipAuthRefresh: true,
      } as AuthRequestConfig);
      onRefreshed();
      return api(originalRequest);
    } catch (refreshError) {
      await handleRefreshFailure();
      onRefreshFailed(refreshError);
      if (!originalRequest._skipAuthRedirect) {
        redirectToLogin();
      }
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  },
);

async function handleRefreshFailure() {
  try {
    await axios.post('/auth/logout', {}, apiConfig);
  } catch {
    // ignore
  }
}
